import map from 'lodash/map';
import React from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';

import { clearSamplesBasket } from 'Apollo/Store/Showroom/UserView/Samples/Basket/resolvers';
import { clearRequestSamplesForm } from 'Apollo/Store/Showroom/UserView/Samples/Form/resolvers';
import { setIsRequestSamplesFormOpen, setIsRequestSamplesValid } from 'Apollo/Store/Showroom/UserView/Samples/resolvers';
import {
	useGetShowroomSamplesFormQuery,
	useGetShowroomSamplesOptionsQuery,
	useRequestSamplesMutation,
	useGetShowroomSamplesBasketQuery
} from 'generated/graphql';

import { useCustomNotifications } from 'lib/Hooks';
import { useIdentifyUser } from 'lib/identifyUser';
import { useGetCurrentShowroomData, useIsShowroomMobileVersion } from 'lib/showroom';
import { useUserConsent } from 'lib/userConsent';

import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import Drawer from '@material-ui/core/Drawer';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Stepper from '@material-ui/core/Stepper';
import Toolbar from '@material-ui/core/Toolbar';

import { ShowroomContext } from 'context/showroom/ShowroomContext';

import { ReactComponent as SampleRequestError } from 'icons/sampleRequestError.svg';
import { ReactComponent as SampleRequestSuccess } from 'icons/sampleRequestSuccess.svg';

import ShowroomSamplesFormStepsAboutRequest from './Steps/AboutRequest';
import ShowroomSamplesFormStepsAboutYou from './Steps/AboutYou';
import ShowroomSamplesFormStepsOptional from './Steps/Optional';
import { useStyles } from './styles';

export type ComponentProps = {};

const ShowroomSamplesForm: React.FC<ComponentProps> = () => {
	const classes = useStyles();
	const { t } = useTranslation();

	const showroom = useGetCurrentShowroomData();
	const { id: showroomId, tenant } = showroom;

	const isMobileVersion = useIsShowroomMobileVersion();
	const [currentStep, setCurrentStep] = React.useState(0);
	const [cookies] = useCookies();
	const { track } = React.useContext(ShowroomContext);
	const { enqueueNotification } = useCustomNotifications();

	const identify = useIdentifyUser();
	const { acceptConsent, onSubmit: onSubmitWithConsent, renderInput } = useUserConsent();
	const {
		data: {
			showroom: {
				userView: {
					samples: { form }
				}
			}
		}
	} = useGetShowroomSamplesFormQuery();

	const {
		data: { showroomSamplesBasket }
	} = useGetShowroomSamplesBasketQuery({ variables: { showroomId } });

	const [requestSamples, { loading: loadingRequest }] = useRequestSamplesMutation();

	const {
		data: {
			showroom: {
				userView: {
					samples: { isRequestSamplesFormOpen, isRequestSamplesFormValid }
				}
			}
		}
	} = useGetShowroomSamplesOptionsQuery();

	const currentStepComponent = React.useMemo(() => {
		switch (currentStep) {
			case 0:
				return <ShowroomSamplesFormStepsAboutRequest showroomId={showroomId} />;
			case 1:
				return <ShowroomSamplesFormStepsAboutYou renderUserConsentInput={renderInput} />;
			case 2:
				return <ShowroomSamplesFormStepsOptional />;
		}
	}, [currentStep, renderInput, showroomId]);

	const headerComponent = React.useMemo(() => {
		return (
			<>
				<Stepper activeStep={currentStep}>
					<Step completed={currentStep > 0}>
						<StepLabel>{!isMobileVersion ? t('showroom.samples.form.about') : null}</StepLabel>
					</Step>
					<Step completed={currentStep > 1}>
						<StepLabel>{!isMobileVersion ? t('showroom.samples.form.aboutYou') : null}</StepLabel>
					</Step>
					<Step completed={currentStep > 2}>
						<StepLabel>{!isMobileVersion ? t('showroom.samples.form.optional') : null}</StepLabel>
					</Step>
				</Stepper>
				<Divider variant="middle" className={classes.headerDivider} />
			</>
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentStep, isMobileVersion, t]);

	const submitForm = React.useCallback(async () => {
		try {
			await acceptConsent(form.email);
			const showroomViewerId = cookies[`showroom-${tenant.tenant}`] ? cookies[`showroom-${tenant.tenant}`].userViewerId : null;
			identify(
				{
					id: form.email,
					email: form.email,
					firstName: form.firstName,
					lastName: form.lastName,
					externalGuest: true
				},
				{
					Site: tenant?.name,
					siteId: tenant?.id,
					ecoAccountId: tenant?.EcosystemAccountID,
					EcoAccountName: tenant?.EcosystemAccountName,
					SalesforceId: tenant?.salesforceAccountId,
					sfparentid: tenant?.salesforceAccountParentId
				}
			);
			const mediaIds = map(showroomSamplesBasket, media => media.id);

			await requestSamples({ variables: { params: form, mediaIds, showroomId, showroomViewerId } });
			if (!!showroomViewerId) {
				const samplesRequestTrack = showroomSamplesBasket.map(media => ({
					eventType: 'samples_request',
					mediaType: 'image',
					mediaId: media.cloudinaryId,
					showroomId: showroomId
				}));
				track(samplesRequestTrack, form.email);
			}
			setIsRequestSamplesFormOpen(false);
			clearSamplesBasket(tenant.uuid, showroomId);
			setCurrentStep(0);
			clearRequestSamplesForm();

			enqueueNotification({
				title: t('showroom.samples.form.requestSent'),
				text: (
					<span
						dangerouslySetInnerHTML={{
							__html: t('showroom.samples.form.requestSentText')
						}}
					></span>
				),
				icon: <SampleRequestSuccess />
			});
		} catch (err) {
			enqueueNotification(
				{
					title: t('showroom.samples.form.errorTitle'),
					text: (
						<span
							dangerouslySetInnerHTML={{
								__html: t('showroom.samples.form.errorContent')
							}}
						></span>
					),
					icon: <SampleRequestError />
				},
				{
					persist: true
				}
			);
			setIsRequestSamplesFormOpen(false);
			setCurrentStep(0);
		}
	}, [acceptConsent, form, cookies, tenant, identify, showroomSamplesBasket, requestSamples, showroomId, enqueueNotification, t, track]);

	const handleCancelClick = React.useCallback(() => {
		if (currentStep === 0) setIsRequestSamplesFormOpen(false);
		else setCurrentStep(currentStep - 1);
	}, [currentStep]);

	const handleNextClick = React.useCallback(() => {
		if (currentStep === 2) {
			setIsRequestSamplesValid(true);
			return submitForm();
		}
		if (currentStep === 1)
			onSubmitWithConsent(() => {
				setCurrentStep(currentStep + 1);
			});
		else setCurrentStep(currentStep + 1);
	}, [currentStep, onSubmitWithConsent, submitForm]);

	return (
		<Drawer
			id="showroomSamplesFormDrawer"
			open={isRequestSamplesFormOpen}
			anchor="right"
			classes={{
				paper: classes.root
			}}
			disableEscapeKeyDown={true}
			onClose={() => setIsRequestSamplesFormOpen(false)}
		>
			<div className={classes.samplesFormContainer}>
				<div className={classes.samplesFormContainerHeader}>{headerComponent}</div>
				<div className={classes.samplesFormContainerSteps}>{currentStepComponent}</div>
				<AppBar position="sticky" className={classes.samplesFormContainerFooter}>
					<Toolbar className={classes.samplesFormContainerFooterToolbar}>
						<div className={classes.samplesFormContainerFooterCancelButtonBox}>
							{loadingRequest ? null : (
								<Button onClick={handleCancelClick}>
									{currentStep === 0 ? t('showroom.samples.form.cancel') : t('showroom.samples.form.back')}
								</Button>
							)}
						</div>
						<div>
							{loadingRequest ? (
								<CircularProgress size={20} />
							) : (
								<Button
									id="showroomSamplesFormNextButton"
									disabled={!isRequestSamplesFormValid}
									variant="contained"
									color="secondary"
									onClick={handleNextClick}
								>
									{t(`showroom.samples.form.${currentStep !== 2 ? 'next' : 'send'}`)}
								</Button>
							)}
						</div>
					</Toolbar>
				</AppBar>
			</div>
		</Drawer>
	);
};

export default React.memo(ShowroomSamplesForm);
